import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useMemo, useState } from "react";
import { PagedTable, type Column } from "../../components/PagedTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { NewsfeedDialog } from "./NewsfeedDialog";
import { useCsrf, useDeleteNewsfeed, useNewsfeeds, type Newsfeed } from "./api";
import { authorLabel } from "./author";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { formatDateTimeWithZone } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { usePageTitle } from "../../app/usePageTitle";
import { DEFAULT_PAGE_SIZE } from "../../lib/pagination";

export function NewsfeedPage() {
  usePageTitle("Newsfeed");
  const { can } = useAuth();
  const notify = useNotify();
  useCsrf();

  const [query, setQuery] = useState({ pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [editing, setEditing] = useState<Newsfeed | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<Newsfeed | null>(null);

  const feed = useNewsfeeds(query);
  const remove = useDeleteNewsfeed();

  const columns = useMemo<Column<Newsfeed>[]>(
    () => [
      { key: "title", label: "Title" },
      {
        key: "created_by",
        label: "Created By",
        render: (row) => authorLabel(row.created_by, {}),
      },
      {
        key: "publish_date",
        label: "Publish date",
        render: (row) => formatDateTimeWithZone(row.publish_date),
      },
      {
        key: "status",
        label: "Status",
        render: (row) =>
          new Date(row.publish_date).getTime() <= Date.now() ? "Published" : "Scheduled",
      },
      {
        key: "actions",
        label: "Actions",
        align: "right",
        pinned: true,
        render: (row) => (
          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            {can(Authority.EditNewsfeed) && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit ${row.title}`}
                  onClick={() => {
                    setEditing(row);
                    setDialogOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.DeleteNewsfeed) && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete ${row.title}`}
                  onClick={() => setDeleting(row)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [can],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}
      >
        {can(Authority.CreateNewsfeed) && (
          <Button
            variant="contained"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            New post
          </Button>
        )}
      </Stack>

      <PagedTable
        columns={columns}
        rows={feed.data?.newsfeeds ?? []}
        rowKey={(row) => row.id}
        total={feed.data?.total ?? 0}
        page={query.pageNumber}
        pageSize={query.pageSize}
        loading={feed.isFetching}
        empty={
          <EmptyState
            filtered={false}
            noun="newsfeed posts"
            action={
              can(Authority.CreateNewsfeed)
                ? {
                    label: "Create your first post",
                    onClick: () => {
                      setEditing(null);
                      setDialogOpen(true);
                    },
                  }
                : undefined
            }
          />
        }
        onPageChange={(pageNumber) => setQuery((previous) => ({ ...previous, pageNumber }))}
        onPageSizeChange={(pageSize) => setQuery({ pageNumber: 0, pageSize })}
      />

      {dialogOpen && (
        <NewsfeedDialog
          key={editing?.id ?? "new"}
          open={dialogOpen}
          existing={editing}
          onClose={() => setDialogOpen(false)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete "${deleting?.title}"?`}
        message="It is removed from the app immediately. This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            notify.success("Post deleted");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Could not delete the post.");
          } finally {
            setDeleting(null);
          }
        }}
      />
    </Box>
  );
}
